import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    GridRenderCellParams,
    GridValueFormatterParams,
} from '@mui/x-data-grid';
import { PersonAdd } from '@mui/icons-material';
import { RootState } from 'store';
import { retrievedNewPortalUsers } from 'store/features/authentication';
import { rolesById } from 'lib/constants';
import {
    getNewPortalUsers,
    getUser,
    getUserProtected,
    removePortalUser,
} from 'lib/firebase';
import PersonRoleCell from './components/data-grid-cells/PersonRoleCell/PersonRoleCell';
import { InviteUserModal } from 'components/modals/InviteUserModal/InviteUserModal';
import styles from './Team.module.scss';
import {
    DataTable,
    DataTableColumn,
} from 'components/data/DataTable/DataTable';
import { OpenModalButton } from 'components/button/OpenModalButton/OpenModalButton';
import {
    FirebaseNewPortalUser,
    FireBaseNewPortalUsersTable,
} from 'types/User/firebaseTypes';
import { activateSnackbar } from 'store/features/ui';
import { useAuth } from 'hooks';
import { RemovePersonCell } from './components/data-grid-cells/RemovePersonCell/RemovePersonCell';
import { ReadOnlyCell } from 'components/DataGridCells/ReadOnlyCell/ReadOnlyCell';
import { lastLoginFilter } from 'components/DataGridCells/data-grid-formatters';

export interface TeamProps {
    /** A supplierId for the supplier that an admin user wants to view data for. */
    viewAsSupplier?: string;
}

/** The Team page showing portal users (i.e., supplier users). */
export function Team({ viewAsSupplier }: TeamProps) {
    const [users, setUsers] = useState<FirebaseNewPortalUser[]>([]);
    const [loading, setLoading] = useState(false);
    const authentication = useSelector(
        (state: RootState) => state.authentication
    );
    const { user } = useAuth();
    const dispatch = useDispatch();

    const onRemoveCellClick = (params: GridRenderCellParams<any>) => {
        if (!user || !user.uid) return;
        removePortalUser(user.uid, params.row.uid).then(() => {
            const supplierId =
                viewAsSupplier ?? user?.protected?.supplierId ?? '';

            if (!supplierId) return;
            getNewPortalUsers(supplierId).then((newPortalUsers) => {
                dispatch(retrievedNewPortalUsers(newPortalUsers));
                dispatch(
                    activateSnackbar({
                        message: 'User successfully removed from supplier.',
                        severity: 'success',
                    })
                );
            });
        });
    };

    const roleIdFilter = (params: GridValueFormatterParams<number>) => {
        return rolesById[params.value];
    };

    const onUserInvitedHandler = () => {
        if (!authentication.supplierId) return;
        getNewPortalUsers(viewAsSupplier ?? authentication.supplierId).then(
            (newPortalUsers) => {
                dispatch(retrievedNewPortalUsers(newPortalUsers));
                dispatch(
                    activateSnackbar({
                        message: 'User successfully invited',
                        severity: 'info',
                    })
                );
            }
        );
    };

    const canEditUser = useCallback(
        (targetUser: any) => {
            if (!user) return false;

            const targetUserRoleId =
                targetUser.protected?.roleId ?? targetUser.roleId;

            // Admin users only have role editing permission if their privs allow for it.
            if (user.protected?.supplierId?.toString() === '0') {
                return user.protected?.privs?.user_edit ?? false;
            } else if (
                user.protected?.roleId !== undefined &&
                targetUserRoleId !== undefined &&
                user.protected?.roleId <= targetUserRoleId && // user has a higher permission role than the role level they are trying to assign to a user.
                user.protected?.roleId <= 1
            ) {
                return true;
            }
            return false;
        },
        [user]
    );

    // the initial queried users are missing their lastLogin and roleId, we must retrieve them from fb.
    const getMissingUserInformation = async (
        portalUsers: FireBaseNewPortalUsersTable
    ) => {
        const promises = Object.entries(portalUsers).map(async ([_, user]) => {
            const uid = user.uid;
            const userProtected = await getUserProtected(uid).then(
                (userProtected) => userProtected
            );
            const roleId = userProtected?.roleId;
            const readOnly = userProtected?.read_only;
            const lastLogin = await getUser(uid).then(
                (user) => user?.info?.lastLoginPortal
            );

            return { uid, roleId, lastLogin, readOnly };
        });

        const resolvedUsers = await Promise.all(promises);

        const newUsers = resolvedUsers.map((user) => {
            const referencedPortalUser = portalUsers[user.uid];
            return {
                ...referencedPortalUser,
                ...user,
            };
        });

        return newUsers;
    };

    useEffect(() => {
        if (!!authentication?.newPortalUsers) {
            setLoading(true);
            getMissingUserInformation(authentication.newPortalUsers).then(
                (resolvedUsers) => {
                    setUsers(resolvedUsers);
                    setLoading(false);
                }
            );
        }
    }, [authentication.newPortalUsers]);

    useEffect(() => {
        if (viewAsSupplier) {
            getNewPortalUsers(viewAsSupplier).then((newPortalUsers) => {
                dispatch(retrievedNewPortalUsers(newPortalUsers));
            });
        } else if (user?.protected?.supplierId !== undefined) {
            getNewPortalUsers(user?.protected?.supplierId).then(
                (newPortalUsers) => {
                    dispatch(retrievedNewPortalUsers(newPortalUsers));
                }
            );
        }
    }, [
        authentication.supplierId,
        dispatch,
        user?.protected?.supplierId,
        viewAsSupplier,
    ]);

    const columns: DataTableColumn[] = [
        {
            field: 'email',
            headerName: 'Email',
            minWidth: 260,
        },
        {
            field: 'status',
            headerName: 'Status',
        },
        {
            field: 'roleId',
            headerName: 'Role ID',
            valueFormatter: roleIdFilter,
            renderCell: (params) => (
                <PersonRoleCell
                    isDisabled={!canEditUser(params.row)}
                    {...params}
                />
            ),
            maxWidth: 250,
        },
        {
            field: 'readOnly',
            fbKey: 'read_only',
            headerName: 'Read Only',
            maxWidth: 250,
            type: 'actions',
            sortingOrder: ['asc', 'desc'],
            renderCell: (params) => (
                <ReadOnlyCell
                    currentUserId={user?.uid}
                    updatedUserId={params.row.uid}
                    roleId={params.row.roleId}
                    readOnly={params.row.readOnly ?? false}
                    isDisabled={!canEditUser(params.row)}
                />
            ),
        },
        {
            field: 'lastLogin',
            headerName: 'Last Login',
            minWidth: 150,
            valueFormatter: lastLoginFilter,
        },
        {
            field: 'uid',
            headerName: 'Remove',
            renderCell: (params) => (
                <RemovePersonCell
                    isDisabled={!canEditUser(params.row)}
                    onClick={() => onRemoveCellClick(params)}
                    userEmail={params.row.email}
                />
            ),
            sortable: false,
            maxWidth: 100,
        },
    ];

    return (
        <div data-testid="Team" className={styles.container}>
            <DataTable
                id="user"
                columns={columns}
                data={users}
                loading={loading}
                dataFormatter={(data) => data}
                getRowId={(user: any) => user.uid}
                toolbarComponents={
                    <OpenModalButton
                        icon={<PersonAdd />}
                        modal={(props) => (
                            <InviteUserModal
                                onUserInvited={onUserInvitedHandler}
                                {...props}
                            />
                        )}
                    >
                        Invite
                    </OpenModalButton>
                }
                initialState={{
                    sorting: {
                        sortModel: [{ field: 'email', sort: 'asc' }],
                    },
                }}
                getRowCount={(data) => data?.length ?? 0}
            />
        </div>
    );
}
